import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Shield, UserPlus, Trash2, Clock, Copy, Send, X } from "lucide-react";

const ROLE_KEYS = ["admin", "manager", "member"];

export default function TeamPage() {
  const { t } = useI18n();
  const { user } = useAuth();
  const canManage = user && (user.role === "owner" || user.role === "admin");

  const [team, setTeam] = useState([]);
  const [invites, setInvites] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", role: "member" });
  const [sending, setSending] = useState(false);
  const [lastLink, setLastLink] = useState("");

  const loadTeam = () => api.get("/team").then(({ data }) => setTeam(data)).catch(() => {});
  const loadInvites = () => {
    if (!canManage) return;
    api.get("/team/invitations").then(({ data }) => setInvites(data)).catch(() => {});
  };

  useEffect(() => { loadTeam(); loadInvites(); /* eslint-disable-next-line */ }, [canManage]);

  const submitInvite = async (e) => {
    e.preventDefault();
    setSending(true);
    setLastLink("");
    try {
      const { data } = await api.post("/team/invite", { ...form, origin_url: window.location.origin });
      toast.success(t("dash.pages.team.invited"));
      if (!data.email_sent) setLastLink(data.invite_link);
      setForm({ name: "", email: "", role: "member" });
      loadInvites();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || t("dash.pages.team.inviteErr"));
    } finally {
      setSending(false);
    }
  };

  const changeRole = async (id, role) => {
    try {
      await api.patch(`/team/${id}/role`, { role });
      toast.success(t("dash.pages.team.roleUpdated"));
      loadTeam();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const removeMember = async (id) => {
    if (!window.confirm(t("dash.pages.team.confirmRemove"))) return;
    try {
      await api.delete(`/team/${id}`);
      toast.success(t("dash.pages.team.removed"));
      loadTeam();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const revoke = async (id) => {
    try {
      await api.post(`/team/invitations/${id}/revoke`);
      toast.success(t("dash.pages.team.revoked"));
      loadInvites();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const copy = (text) => {
    navigator.clipboard?.writeText(text);
    toast.success(t("dash.pages.team.copied"));
  };

  return (
    <div className="space-y-8 max-w-5xl" data-testid="team-page">
      <div>
        <h1 className="text-3xl font-extrabold text-white">{t("dash.pages.team.title")}</h1>
        <p className="mt-1 text-white/55">{t("dash.pages.team.desc")}</p>
      </div>

      {/* Invite */}
      {canManage && (
        <div className="rounded-lg border border-white/10 bg-[#0a0a0a] p-6" data-testid="invite-card">
          <h2 className="text-lg font-bold text-white flex items-center gap-2">
            <UserPlus className="w-5 h-5 text-[#EAB308]" /> {t("dash.pages.team.invite")}
          </h2>
          <p className="mt-1 text-sm text-white/50">{t("dash.pages.team.inviteDesc")}</p>
          <form onSubmit={submitInvite} className="mt-5 grid gap-3 sm:grid-cols-[1fr_1fr_auto_auto] sm:items-end">
            <div className="space-y-1.5">
              <Label className="text-white/70 text-xs">{t("dash.pages.team.name")}</Label>
              <Input
                data-testid="invite-name"
                required value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                className="bg-black/40 border-white/15 text-white h-11"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-white/70 text-xs">{t("dash.pages.team.email")}</Label>
              <Input
                data-testid="invite-email"
                required type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                className="bg-black/40 border-white/15 text-white h-11"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-white/70 text-xs">{t("dash.pages.team.role")}</Label>
              <select
                data-testid="invite-role"
                value={form.role} onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}
                className="h-11 rounded-md bg-black/40 border border-white/15 text-white px-3 text-sm w-full sm:w-40"
              >
                {ROLE_KEYS.map((r) => (
                  <option key={r} value={r} className="bg-[#0a0a0a]">{t(`dash.pages.team.roles.${r}`)}</option>
                ))}
              </select>
            </div>
            <Button
              data-testid="invite-submit"
              type="submit" disabled={sending}
              className="h-11 bg-[#EAB308] text-black font-semibold rounded-md gap-2 hover:opacity-90"
            >
              <Send className="w-4 h-4" />
              {sending ? t("dash.pages.team.sending") : t("dash.pages.team.send")}
            </Button>
          </form>

          {lastLink && (
            <div className="mt-4 rounded-md border border-[#EAB308]/30 bg-[#EAB308]/[0.06] p-3" data-testid="invite-link-box">
              <p className="text-xs text-[#EAB308] mb-2">{t("dash.pages.team.emailSentNo")}</p>
              <div className="flex items-center gap-2">
                <code className="flex-1 truncate text-xs text-white/70 bg-black/40 rounded px-2 py-1.5">{lastLink}</code>
                <Button data-testid="copy-invite-link" onClick={() => copy(lastLink)} className="h-8 px-3 bg-white/10 border border-white/15 text-white text-xs gap-1.5 hover:bg-white/15">
                  <Copy className="w-3.5 h-3.5" /> {t("dash.pages.team.copyLink")}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Members */}
      <div>
        <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-white/40 mb-3">{t("dash.pages.team.members")}</div>
        <div className="rounded-lg border border-white/10 bg-[#0a0a0a] divide-y divide-white/5">
          {team.map((m) => {
            const initials = m.name?.split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();
            const isOwner = m.role === "owner";
            return (
              <div key={m.id} className="flex items-center gap-4 p-5" data-testid="team-member">
                <span className="flex h-11 w-11 items-center justify-center rounded-md text-sm font-bold text-black shrink-0" style={{ background: m.avatar_color || "#EAB308" }}>
                  {initials}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-white truncate flex items-center gap-2">
                    {m.name}
                    {m.is_self && <span className="text-[10px] font-mono uppercase tracking-wider text-white/40 border border-white/15 rounded px-1.5 py-0.5">{t("dash.pages.team.you")}</span>}
                  </div>
                  <div className="text-sm text-white/45 truncate">{m.email}</div>
                </div>

                {canManage && !isOwner ? (
                  <select
                    data-testid={`role-select-${m.id}`}
                    value={m.role}
                    onChange={(e) => changeRole(m.id, e.target.value)}
                    className="h-9 rounded-md bg-black/40 border border-white/15 text-white px-2.5 text-xs"
                  >
                    {ROLE_KEYS.map((r) => (
                      <option key={r} value={r} className="bg-[#0a0a0a]">{t(`dash.pages.team.roles.${r}`)}</option>
                    ))}
                  </select>
                ) : (
                  <span className="inline-flex items-center gap-1.5 rounded-full border border-[#EAB308]/30 bg-[#EAB308]/10 px-3 py-1 text-xs font-medium text-[#EAB308]">
                    <Shield className="w-3.5 h-3.5" />
                    {t(`dash.pages.team.roles.${m.role}`)}
                  </span>
                )}

                {canManage && !isOwner && !m.is_self && (
                  <button
                    data-testid={`remove-member-${m.id}`}
                    onClick={() => removeMember(m.id)}
                    className="flex h-9 w-9 items-center justify-center rounded-md border border-white/10 text-white/50 hover:text-[#dc143c] hover:border-[#dc143c]/40 transition-colors"
                    aria-label="remove"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
        {canManage && <p className="mt-3 text-xs text-white/30">{t("dash.pages.team.manageHint")}</p>}
      </div>

      {/* Pending invitations */}
      {canManage && (
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-white/40 mb-3">{t("dash.pages.team.pending")}</div>
          <div className="rounded-lg border border-white/10 bg-[#0a0a0a] divide-y divide-white/5" data-testid="pending-invitations">
            {invites.length === 0 ? (
              <div className="p-5 text-sm text-white/40">{t("dash.pages.team.noPending")}</div>
            ) : (
              invites.map((i) => (
                <div key={i.id} className="flex items-center gap-4 p-5" data-testid="pending-invite">
                  <span className="flex h-10 w-10 items-center justify-center rounded-md bg-white/5 border border-white/10 text-white/40 shrink-0">
                    <Clock className="w-4 h-4" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-white truncate">{i.email}</div>
                    <div className="text-xs text-white/45">
                      {t(`dash.pages.team.roles.${i.role}`)}
                      {i.expired && <span className="ml-2 text-[#dc143c]">· {t("dash.pages.team.expired")}</span>}
                    </div>
                  </div>
                  <button
                    data-testid={`revoke-invite-${i.id}`}
                    onClick={() => revoke(i.id)}
                    className="inline-flex items-center gap-1.5 rounded-md border border-white/10 px-3 py-1.5 text-xs text-white/60 hover:text-[#dc143c] hover:border-[#dc143c]/40 transition-colors"
                  >
                    <X className="w-3.5 h-3.5" /> {t("dash.pages.team.revoke")}
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
